import { DeleteReleasePolicy } from '@gonfalon/openapi/operations/DeleteReleasePolicy';
import { GetFlagReleaseSettings } from '@gonfalon/openapi/operations/GetFlagReleaseSettings';
import { GetReleasePolicies } from '@gonfalon/openapi/operations/GetReleasePolicies';
import { GetReleasePolicy } from '@gonfalon/openapi/operations/GetReleasePolicy';
import { PostReleasePoliciesOrder } from '@gonfalon/openapi/operations/PostReleasePoliciesOrder';
import { PostReleasePolicy } from '@gonfalon/openapi/operations/PostReleasePolicy';
import { PutReleasePolicy } from '@gonfalon/openapi/operations/PutReleasePolicy';
import type { ReleasePoliciesResponse } from '@gonfalon/openapi/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

// fetch a list of release policies
export const getReleasePoliciesQuery = createQueryOptions(GetReleasePolicies);

export const getFlagReleaseSettingsQuery = createQueryOptions(GetFlagReleaseSettings, {
  retry: false,
  staleTime: 1000 * 60,
});

// fetch a single release policy
export const getReleasePolicyQuery = createQueryOptions(GetReleasePolicy);

// create a new release policy
export const useCreateReleasePolicy = createMutationHook(PostReleasePolicy, {
  onSuccess: async (_, { projectKey }) => {
    const queryClient = getQueryClient();
    await queryClient.refetchQueries({ queryKey: getReleasePoliciesQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getReleasePolicyQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getFlagReleaseSettingsQuery.partialQueryKey({ projectKey }) });
  },
});

// update a release policy
export const useUpdateReleasePolicy = createMutationHook(PutReleasePolicy, {
  onSuccess: async (_, { projectKey }) => {
    const queryClient = getQueryClient();
    await queryClient.refetchQueries({ queryKey: getReleasePoliciesQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getReleasePolicyQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getFlagReleaseSettingsQuery.partialQueryKey({ projectKey }) });
  },
});

// delete a release policy
export const useDeleteReleasePolicy = createMutationHook(DeleteReleasePolicy, {
  onSuccess: async (_, { projectKey }) => {
    const queryClient = getQueryClient();
    await queryClient.refetchQueries({ queryKey: getReleasePoliciesQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getReleasePolicyQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getFlagReleaseSettingsQuery.partialQueryKey({ projectKey }) });
  },
});

interface ReleasePoliciesOptimisticUpdateContext {
  previousReleasePolicies: ReleasePoliciesResponse | undefined;
  queryKey: readonly unknown[];
}

/**
 * Reorders release policy items based on the requested order.
 * Items not included in requestedPolicyOrder maintain their relative position at the end of the array.
 * Note: The default policy is excluded from the API payload and rendered separately in the UI,
 * so its position in this array does not affect the visual order.
 */
function reorderReleasePolicyItems(
  items: ReleasePoliciesResponse['items'],
  requestedPolicyOrder: string[],
): ReleasePoliciesResponse['items'] {
  return [...items].sort((policyA, policyB) => {
    const policyAOrderIndex = requestedPolicyOrder.indexOf(policyA.key);
    const policyBOrderIndex = requestedPolicyOrder.indexOf(policyB.key);

    const policyAIsNotInRequestedOrder = policyAOrderIndex === -1;
    const policyBIsNotInRequestedOrder = policyBOrderIndex === -1;

    if (policyAIsNotInRequestedOrder && policyBIsNotInRequestedOrder) {
      return 0;
    }
    if (policyAIsNotInRequestedOrder) {
      return 1;
    }
    if (policyBIsNotInRequestedOrder) {
      return -1;
    }
    return policyAOrderIndex - policyBOrderIndex;
  });
}

// reorder release policies
export const useReorderReleasePolicies = createMutationHook(PostReleasePoliciesOrder, {
  onMutate: async (variables): Promise<ReleasePoliciesOptimisticUpdateContext> => {
    const queryClient = getQueryClient();
    const queryKey = getReleasePoliciesQuery({
      projectKey: variables.projectKey,
      header: { 'LD-API-Version': 'beta' },
    }).queryKey;

    await queryClient.cancelQueries({ queryKey });

    const previousReleasePolicies = queryClient.getQueryData<ReleasePoliciesResponse>(queryKey);

    const requestedPolicyOrder = variables.body;

    if (requestedPolicyOrder && Array.isArray(requestedPolicyOrder) && previousReleasePolicies) {
      const reorderedData: ReleasePoliciesResponse = {
        ...previousReleasePolicies,
        items: reorderReleasePolicyItems(previousReleasePolicies.items, requestedPolicyOrder),
      };

      queryClient.setQueryData<ReleasePoliciesResponse>(queryKey, reorderedData);
    }

    return { previousReleasePolicies, queryKey };
  },
  onError: (_, __, context?: ReleasePoliciesOptimisticUpdateContext) => {
    const queryClient = getQueryClient();
    if (context?.queryKey) {
      queryClient.setQueryData(context.queryKey, context.previousReleasePolicies);
    }
  },
  onSettled: async (_, __, variables) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: getReleasePoliciesQuery({
        projectKey: variables.projectKey,
        header: { 'LD-API-Version': 'beta' },
      }).queryKey,
    });
    await queryClient.invalidateQueries({
      queryKey: getFlagReleaseSettingsQuery.partialQueryKey({ projectKey: variables.projectKey }),
    });
  },
});
